// The one resolution of an enum value's colour (#11426, keeper ruling,
// round 5; amended by the CD org-color ruling).
//
// The renderer never consumes a raw org hex: an org-supplied hex arriving at
// a paint site is a colour named below the theme — the same defect as a
// hardcoded hex in a component, posted in from data (rule 1). Colour comes
// from the token ramp, full stop. What the org DOES own is the hue: a
// configured enum_colors[value] is a hue request, snapped onto the ramp
// (hueSnap.ts) so the Settings picker and the grid finally agree:
//
//   - a STATUS field's value paints, in order of precedence, its configured
//     color's snapped hue (in the judgement FORM), else its mapped semantic
//     tone, else `none`;
//   - a value-hued vocabulary's value paints its configured color's snapped
//     hue, else the ramp hue derived from the value itself;
//   - every other enum value takes the categorical ramp derived from the
//     value (or the field, for a plain enum);
//   - a key outside the fact's declared vocabulary takes the none tone — no
//     category judgement was made, and the grey says so.
//
// Token tones carry designed fill-contrast floors, which is what closes the
// "some chips read as bare text" gap an arbitrary hex at 7% alpha produced.

import { hueFor } from "./tagHue";
import { snapHexToRampHue, type RampHue } from "./hueSnap";
import { isSemanticState, type SemanticState } from "./semanticState";

export interface EnumToneStyles {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  /**
   * Present when the tone is a JUDGEMENT (a status fact's value, or the
   * none tone). Dot-scale renderers distinguish `none` from `closed` by
   * FORM — a hollow ring versus a filled dot — because both are greys and
   * lightness alone does not survive an 8px dot (keeper ruling, round 5).
   * `hued` marks a status value whose colour came from the org's configured
   * pick rather than a semantic mapping: still a judgement (bordered pill,
   * filled dot), just one whose hue the org named.
   */
  state?: SemanticState | "hued";
}

/** The judgement tones — same tokens the StatusChip speaks. */
export function semanticToneStyles(state: SemanticState): EnumToneStyles {
  return {
    backgroundColor: `var(--fp-st-${state}-bg)`,
    borderColor: `var(--fp-st-${state}-bd)`,
    textColor: `var(--fp-st-${state})`,
    state,
  };
}

/** The categorical ramp, derived from the value — never assigned. */
export function categoryToneStyles(value: string): EnumToneStyles {
  const hue = hueFor(value);
  return {
    backgroundColor: `var(--fp-tag-${hue}-bg)`,
    borderColor: "transparent",
    textColor: `var(--fp-tag-${hue})`,
  };
}

/** A configured color's snapped hue, worn as a tag: borderless wash. */
function snappedTagToneStyles(hue: RampHue): EnumToneStyles {
  return {
    backgroundColor: `var(--fp-tag-${hue}-bg)`,
    borderColor: "transparent",
    textColor: `var(--fp-tag-${hue})`,
  };
}

/**
 * A configured color's snapped hue, worn as a STATUS: the judgement form —
 * tinted, bordered, dotted — with the hue the org picked. The border mixes
 * the hue at the same 30% the semantic tones' `-bd` tokens carry.
 */
function snappedStatusToneStyles(hue: RampHue): EnumToneStyles {
  return {
    backgroundColor: `var(--fp-tag-${hue}-bg)`,
    borderColor: `color-mix(in srgb, var(--fp-tag-${hue}) 30%, transparent)`,
    textColor: `var(--fp-tag-${hue})`,
    state: "hued",
  };
}

// A plain single-choice enum's hue derives from the FIELD, not the value
// (design round 8): its values are attributes, not a colour-coded taxonomy,
// and hashing the value landed "Closed" on the pos green and an investor
// type on the watch amber — judgements nobody made, sitting beside real
// status pills wearing the same hues. The reduced ramp excludes the hues
// the judgement scale reads as verdicts, so no value of a plain enum can
// impersonate one; two fields still differ from each other because the
// field name drives the hash.
const FIELD_HUES = ["blue", "teal", "purple", "slate"] as const;

export function fieldToneStyles(fieldKey: string): EnumToneStyles {
  let hash = 0;
  for (const char of String(fieldKey)) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  const hue = FIELD_HUES[hash % FIELD_HUES.length];
  return {
    backgroundColor: `var(--fp-tag-${hue}-bg)`,
    borderColor: "transparent",
    textColor: `var(--fp-tag-${hue})`,
  };
}

/** No judgement made: outside the vocabulary, or an unmapped status word. */
export function noneToneStyles(): EnumToneStyles {
  return semanticToneStyles("none");
}

/**
 * The tone for one enum value.
 *
 * `statusStates` present marks the fact as the kind's status field: the
 * value's configured color (snapped, in the judgement form) paints it, else
 * its mapped semantic state, else `none` (no mapping means we were not told
 * what the word means, and guessing a judgement is worse than declining to
 * show one). Otherwise the value is a category: in-vocabulary keys take the
 * configured color's snapped hue when one exists, else the derived ramp;
 * out-of-vocabulary keys take the none tone.
 */
export function enumValueToneStyles(
  value: string,
  opts: {
    statusStates?: Record<string, string> | null;
    inVocabulary?: boolean;
    // The CATEGORY the value belongs to, when the org groups its values
    // (tag categories). The hue derives from the category name so two
    // values in one category share a colour — "tag colour means category"
    // held structurally, still without any org hex.
    categoryKey?: string | null;
    // The field the value belongs to. When the vocabulary does not
    // distinguish its values by hue (`valueHued` false), the hue derives
    // from the field on the reduced ramp — see fieldToneStyles.
    fieldKey?: string | null;
    // The vocabulary is a colour-coded taxonomy — the org configured
    // per-value colours (interaction types, document types), so hue
    // distinguishing VALUES is the point.
    valueHued?: boolean;
    // The org's configured per-value colors, consumed as hue requests only
    // (hueSnap.ts) — the raw hex still never paints. A value this map does
    // not name (or names unparseably) keeps the un-configured behavior.
    enumColors?: Record<string, string> | null;
  } = {}
): EnumToneStyles {
  const configuredHue = (): RampHue | null => {
    const hex = opts.enumColors?.[value];
    return hex ? snapHexToRampHue(hex) : null;
  };
  if (opts.statusStates) {
    const hue = configuredHue();
    if (hue) return snappedStatusToneStyles(hue);
    const mapped = opts.statusStates[value];
    return semanticToneStyles(isSemanticState(mapped) ? mapped : "none");
  }
  if (opts.inVocabulary === false) return noneToneStyles();
  if (opts.categoryKey) return categoryToneStyles(opts.categoryKey);
  // A configured color for THIS value snaps whatever the flags say — a
  // vocabulary with configured colors is value-hued by definition, and
  // checking here keeps call sites that pass partial opts (the filter
  // dots) agreeing with the grid.
  const hue = configuredHue();
  if (hue) return snappedTagToneStyles(hue);
  if (opts.valueHued) {
    // Uncolored values of a hued vocabulary keep the stable value hash —
    // never steered away from claimed hues (CD: hue stability under config
    // change beats guaranteed separation).
    return categoryToneStyles(value);
  }
  if (opts.fieldKey) return fieldToneStyles(opts.fieldKey);
  return categoryToneStyles(value);
}

/**
 * The tone annotations an editor dropdown reads from its fact definition.
 * Absent = untoned options (a boolean dropdown, a free-typed vocabulary).
 */
export interface ToneSource {
  status_states?: Record<string, string> | null;
  category_names?: Record<string, string> | null;
  /** The field's identity, for the field-derived hue of a plain enum. */
  name?: string | null;
  /** The org configured per-value colours — a value-hued taxonomy. */
  enum_colors?: Record<string, string> | null;
}

/** One option's tone in an editor dropdown, or null when the list is untoned. */
export function toneForOption(
  option: string,
  source?: ToneSource | null
): EnumToneStyles | null {
  if (!source) return null;
  return enumValueToneStyles(option, {
    statusStates: source.status_states,
    categoryKey: source.category_names?.[option],
    fieldKey: source.name,
    valueHued: Boolean(
      source.enum_colors && Object.keys(source.enum_colors).length > 0
    ),
    enumColors: source.enum_colors,
  });
}

/** Whether a dot-scale rendering of this tone is a hollow ring (`none`). */
export function isHollowTone(styles: EnumToneStyles): boolean {
  return styles.state === "none";
}
